// src/components/features/config/columns/consumers/calls.column.config.tsx
import { Box, Tooltip } from '@mui/material'
import MiniChart from '@/components/Table/MiniChart'
import CrmStatus from '@/components/Table/CrmStatus'
import { CustomColumnGridConfig } from '../types'
import { formatConsumptionValue } from '@/utils/consumptionFormat'

import EmailIcon from '../../../../../assets/chanellsIcons/EmailIcon.svg'
import WhatsappIcon from '../../../../../assets/chanellsIcons/WhatsappIcon.svg'
import SmsIcon from '../../../../../assets/chanellsIcons/SmsIcon.svg'
import MeterLink from '@/components/Table/MeterLink'

const iconMap: Record<string, string> = {
  EmailIcon,
  WhatsappIcon,
  SmsIcon,
}

export const CONSUMPTION_FIELD = 'consumption' as const

const columnsWithConsumption: CustomColumnGridConfig[] = [
  {
    field: 'crm',
    headerName: 'ניהול משימות',
    width: 150,
    renderCell: (params: any) =>
      params.value ? <CrmStatus value={params.value} /> : '-',
    filter: { type: 'options' },
    hideable: true,
    disableAggregation: true,
    filterable: true,
  },
  {
    field: 'sent',
    headerName: 'נשלח',
    filter: { type: 'none' },
    renderCell: (params: any) => {
      const channels: Array<{ channel: string; icon: string }> =
        params.row?.sentChannels ?? []
      if (!channels.length) return '-'
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {channels.map((c, i) => (
            <Tooltip key={i} title={c.channel}>
              <img
                src={iconMap[c.icon]}
                alt={c.channel}
                style={{ width: 18, height: 18 }}
              />
            </Tooltip>
          ))}
        </Box>
      )
    },
  },
  {
    field: 'diameter',
    headerName: 'קוטר',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'options' },
    filterable: true,
  },
  {
    field: 'capacity',
    headerName: 'ק. חצות',
    type: 'number',
    aggregable: true,
    hideable: true,
    renderCell: (params: any) => <div>{params.value ?? '-'}</div>,
    filter: { type: 'aggregated' },
    filterable: true,
  },

  // NEW numeric column: "צריכה" (sum of dailyUsageData) — safe guards included
  {
    field: CONSUMPTION_FIELD,
    headerName: 'צריכה',
    type: 'number',
    width: 120,
    valueGetter: (params: any) => {
      const arr = (params as any)?.row?.dailyUsageData as number[] | undefined
      if (!Array.isArray(arr) || arr.length === 0) return 0
      return arr.reduce((s, n) => s + (Number(n) || 0), 0)
    },
    renderCell: (params: any) => (
      <div style={{ fontWeight: 700 }}>
        {formatConsumptionValue(params.value, 3)}
      </div>
    ),
    aggregable: true,
    filter: { type: 'aggregated' },
    filterable: true,
    hideable: true,
  },
  {
    field: 'dailyUsageData',
    headerName: 'צריכה יומית',
    renderCell: (params: any) =>
      params.value != null ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <MiniChart data={params.value} />
        </Box>
      ) : '-',
    filter: { type: 'aggregated' },
    disableAggregation: true,
    hideable: true,
  },
  {
    field: 'address',
    headerName: 'כתובת',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'search' },
    filterable: true,
  },
  {
    field: 'waterCard',
    headerName: 'כרטיס מים',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'search' },
    filterable: true,
  },
  {
    field: 'name',
    headerName: 'שם צרכן',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'search' },
    filterable: true,
  },
  {
    field: 'id',
    headerName: "מס' מד",
    hideable: true,
    renderCell: (params: any) =>
      params.value != null ? <MeterLink meter={params.value} /> : '-',
    filter: { type: 'search' },
    filterable: true,
  },
  {
    field: 'payerNumber',
    headerName: 'מספר משלם',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'search' },
    filterable: true,
  },
  {
    field: 'contractNumber',
    headerName: 'מספר חוזה',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'none' },
  },
  {
    field: 'tenantCount',
    headerName: 'כמות דיירים',
    hideable: true,
    filter: { type: 'none' },
  },
  {
    field: 'installDate',
    headerName: 'תאריך התקנה',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'none' },
    filterable: true,
  },
  {
    field: 'parentCard',
    headerName: 'כרטיס אב',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'search' },
  },
  {
    field: 'area',
    headerName: 'אזור',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'options' },
    filterable: true,
  },
  {
    field: 'phone',
    headerName: 'מספר טלפון',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'search' },
    filterable: true,
  },
  {
    field: 'email',
    headerName: 'אימייל',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'search' },
    filterable: true,
  },
  {
    field: 'branch',
    headerName: 'ענף',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'none' },
  },
  {
    field: 'issuer',
    headerName: 'יצרן מד',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'options' },
    filterable: true,
  },
  {
    field: 'openCycle',
    headerName: 'מעגל פתוח',
    hideable: true,
    disableAggregation: true,
    filter: { type: 'none' },
    filterable: true,
  },
]

export default columnsWithConsumption
